import { format } from "d3-format";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { ScatterplotAuto } from "../charts-motion/scatterplot";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_aid_fig_AID_BASIC";
import { datasets } from "../data/data_finance_aid_fig_AID_BASIC_datasets";
import { countryIdIso, getCountryMeta, getRegionName, mkGemEntityDecoder, mkOrdRegion, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities } from "../domain";
import { useMedia } from "../hooks";
import { catOptions, extentNumber, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_finance_aid_fig_AID_BASIC";
const DEFAULT_COUNTRIES = [
    "MAR",
    "BFA",
    "AGO"
];
export const Data = mkGemEntityDecoder([
    "year"
], {
    AID_PERCAPITA: io.number,
    POP_SCH: io.number
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenLDown = !useMedia(breakpoints.l);
    const env = useConfig();
    const [, actions] = useFigureState();
    const [selectedCountries] = useMultiSelectState("countries");
    const entityData = useNamedEntities(dataRaw);
    const countryData_ = useCountryEntities(entityData);
    const countryData = React.useMemo(()=>{
        const data = countryData_.map((x)=>{
            return pipe(getCountryMeta(env.countries, x.id), O.chain((meta)=>pipe(O.fromNullable(meta.income), O.fold(()=>O.none, (income)=>O.some({
                        ...meta,
                        income
                    })))), O.map((meta)=>({
                    ...x,
                    region: meta.income,
                    region_name: getRegionName(env.regions, meta.income)
                })));
        });
        return pipe(data, catOptions, Ar.sortBy([
            mkOrdRegion(env.regions, (x)=>x.region),
            Ord.contramap((x)=>x.year)(Ord.ordNumber)
        ]));
    }, [
        env.countries,
        env.regions,
        countryData_
    ]);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    const yearExtent = React.useMemo(()=>extentNumber(unsafeFromArray(countryData), (x)=>x.year), [
        countryData
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            minItems: 0,
            maxItems: 5,
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
    }, [
        intl,
        actions,
        countryControlItems,
        yearExtent
    ]);
    const chartData = unsafeFromArray(countryData);
    const getX = React.useCallback((x)=>x?.POP_SCH, []);
    const getY = React.useCallback((x)=>x?.AID_PERCAPITA, []);
    const getColor = React.useCallback((x)=>`${x.year}`, []);
    const getGroup = React.useCallback((x)=>countryIdIso.unwrap(x.id), []);
    const formatColor = React.useCallback((x)=>`${x.year}`, []);
    const formatGroup = React.useCallback((x)=>x.entity_name, []);
    const xTicks = React.useMemo(()=>[
            0,
            30
        ], []);
    const yTicks = React.useMemo(()=>[
            0,
            80
        ], []);
    return <div>
      <ScatterplotAuto height={screenLDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getY={getY} getColor={getColor} getGroup={getGroup} xLabel={intl.formatMessage(defineMessage({
        id: "POP_SCH",
        defaultMessage: "School age population (millions)"
    }))} yLabel={intl.formatMessage(defineMessage({
        id: "AID_PERCAPITA",
        defaultMessage: "Per capita aid to education, US$"
    }))} formatX={format(".0f")} formatY={format(".0f")} formatColor={formatColor} formatGroup={formatGroup} xTicks={xTicks} yTicks={yTicks} // Year doesn't matter in this case, but the chart needs a value.
    selectedYear={2022} displaySelectedYear={false} highlightedGroups={selectedCountries} onSelect={(x)=>actions.toggleMultiSelectItem("countries", countryIdIso.unwrap(x.id))} displayColorLegend={false}/>
    </div>;
};
export default withFigureIO({
    url: new URL("../data/data_finance_aid_fig_AID_BASIC.json", import.meta.url).href,
    csv: new URL("../data/data_finance_aid_fig_AID_BASIC.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_aid_fig_AID_BASIC.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
